/**
 * fetchAuthWrapper.js — `_kmRefreshWrapped` window.fetch hook.
 *
 * Design (2026-08-06 rebuild, requested 2026-08-16):
 *   • Runs alongside the axios interceptors — axios still refreshes on
 *     401 for its own callers; this wrapper is for the raw `fetch(...)`
 *     callers the axios interceptor cannot see: `mintBlobToken` (used by
 *     FullScreenContentViewer and every FileCard), `pdfPreview.js`'s
 *     bytes fetch, `authedFile.js::_authedFetchBlob`, plus any future
 *     raw-fetch caller.
 *   • Concurrent 401s share a SINGLE refresh flight via
 *     `sessionRefresh.sharedRefresh()`, which is the same promise axios
 *     awaits — two hooks + N callers = one refresh POST.
 *   • The retry CLONES the Request BEFORE the first send.  A body is a
 *     single-use stream; a replayed multipart upload without a fresh
 *     clone truncates.  ReadableStream bodies cannot be cloned reliably
 *     across all browsers and bypass the wrapper entirely.
 *   • Skip by IDENTITY, not presence — see `classifyCredential`.
 */

import { classifyCredential, sharedRefresh, currentTenantTokens } from './sessionRefresh';

let _installed = false;
let _originalFetch = null;

// The default axios instance already runs a request interceptor that
// stamps `Authorization: Bearer …` on every request.  A raw fetch does
// NOT — so the wrapper doubles as the stamping point for the raw-fetch
// callers the axios interceptor cannot see.  Stamping is opt-in: we
// only stamp when the caller has NOT already provided an Authorization
// header, so authedFile.js's per-request contractor/tenant selection
// is preserved verbatim.

export function installKmRefreshWrapped() {
  if (_installed) return;
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  _originalFetch = window.fetch.bind(window);

  window.fetch = async function _kmRefreshWrapped(input, init) {
    // Detect a ReadableStream body — those cannot be safely cloned.
    // For those the wrapper is a straight pass-through: the caller is
    // on its own if it gets a 401.
    const initBody = init && init.body;
    const isStreamBody =
      typeof ReadableStream !== 'undefined' && initBody instanceof ReadableStream;
    if (isStreamBody) {
      return _originalFetch(input, init);
    }

    // Normalise into a Request so headers/method/url/body all live in
    // one place and clone() Just Works for FormData / Blob / string /
    // ArrayBuffer / URLSearchParams.
    //
    // 2026-08-16 iter386τ regression fix — for CLASSIFICATION we build
    // a temporary Request, but for the pass-through 'skip' branch we
    // hand the ORIGINAL input/init back to `_originalFetch`.  Building
    // a Request from a raw URL string coerces `mode`/`credentials`/
    // `referrerPolicy` to spec defaults, which changed how the
    // Emergent platform's own weather widget (`emergent-main.js`)
    // reached its cross-origin endpoint and it started returning
    // `TypeError: Failed to fetch`.  Foreign fetches must be
    // untouched — full stop.
    let req;
    let classifyOk = true;
    try {
      req = (input instanceof Request) ? input : new Request(input, init);
    } catch (_e) {
      // Fall back to a raw pass-through if the Request constructor
      // rejects the input (e.g. certain older browsers with a header
      // object containing forbidden names).
      classifyOk = false;
    }
    if (!classifyOk) {
      return _originalFetch(input, init);
    }

    // Classify BEFORE the first send so we know whether we need to
    // retain a clone for retry.
    const kind = classifyCredential({ url: req.url, headers: req.headers });

    if (kind === 'skip') {
      // No stamping, no refresh — verbatim pass-through of the
      // ORIGINAL input/init, not the reconstructed Request.
      return _originalFetch(input, init);
    }

    // Tenant.  Retain a clone for a possible retry.  `req.clone()`
    // gives a fresh Request with a fresh body stream sharing the
    // underlying bytes, so a replayed upload uploads the SAME bytes,
    // not zero bytes.
    let retryClone;
    try { retryClone = req.clone(); } catch (_e) { retryClone = null; }

    const firstResp = await _originalFetch(req);
    if (firstResp.status !== 401) return firstResp;

    // 401 on a tenant-classified request.  Try to refresh (single
    // shared flight) and replay ONCE.  If the refresh fails, return
    // the original 401 so the caller's own error handling still
    // fires — do NOT force-logout from here; the axios interceptor
    // owns that decision.
    let newToken;
    try {
      newToken = await sharedRefresh();
    } catch (_e) {
      return firstResp;
    }
    if (!newToken || !retryClone) return firstResp;

    // Restamp Authorization on the clone.  Because Request.headers is
    // immutable in the spec (mode may make it so in practice), we
    // rebuild a Request from the clone with fresh headers.
    let retryReq;
    try {
      const newHeaders = new Headers(retryClone.headers);
      newHeaders.set('Authorization', `Bearer ${newToken}`);
      retryReq = new Request(retryClone, { headers: newHeaders });
    } catch (_e) {
      // If for some reason we cannot rebuild the Request with fresh
      // headers, return the original 401 rather than replay with a
      // stale bearer.
      return firstResp;
    }

    return _originalFetch(retryReq);
  };

  // Marker for the audit report and for e2e tests.  Does NOT change
  // behaviour — the wrapper is already installed at this point.
  try {
    window._kmRefreshWrapped = true;
  } catch (_e) { /* ignore */ }

  _installed = true;
}

// Exposed for tests only — restores the original window.fetch and
// permits re-install.
export function _uninstallKmRefreshWrappedForTests() {
  if (!_installed) return;
  if (typeof window !== 'undefined' && _originalFetch) {
    try { window.fetch = _originalFetch; } catch (_e) { /* ignore */ }
    try { delete window._kmRefreshWrapped; } catch (_e) { /* ignore */ }
  }
  _installed = false;
  _originalFetch = null;
}

// Re-export the classifier so tests can exercise it directly.
export { classifyCredential, currentTenantTokens };
